import React, { useState } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import {
  HeartHandshake, Mail, Phone, Building2, User, MapPin, Gift,
  CheckCircle, ArrowRight, Heart, Users, Package, Clock, Send,
  Sparkles, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const DISTRITOS_PORTUGAL = [
  'Aveiro', 'Beja', 'Braga', 'Bragança', 'Castelo Branco', 'Coimbra',
  'Évora', 'Faro', 'Guarda', 'Leiria', 'Lisboa', 'Portalegre', 'Porto',
  'Santarém', 'Setúbal', 'Viana do Castelo', 'Vila Real', 'Viseu',
  'Açores', 'Madeira'
];

const TIPOS_DOADOR = [
  { value: 'Individual', label: 'Pessoa Individual', icon: User },
  { value: 'Empresa', label: 'Empresa', icon: Building2 },
  { value: 'Fundação', label: 'Fundação', icon: Heart },
  { value: 'Associação', label: 'Associação', icon: Users }
];

const TIPOS_AJUDA = [
  { value: 'Produto', label: 'Doação de Produtos', desc: 'Alimentos, roupa, materiais escolares...' },
  { value: 'Financeira', label: 'Doação Financeira', desc: 'Contribuição monetária' },
  { value: 'Voluntariado', label: 'Voluntariado', desc: 'Doar o seu tempo e competências' },
  { value: 'Equipamento', label: 'Equipamentos', desc: 'Mobiliário, eletrodomésticos...' }
];

export default function QueroDoarePage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    tipo_doador: 'Individual',
    nome: '',
    email_principal: '',
    telefone: '',
    distrito: '',
    concelho: '',
    nif: '',
    empresa_nome: '',
    tipos_ajuda: [],
    preferencias_categoria: '',
    disponibilidade: '',
    mensagem: '',
    aceita_comunicacoes: true,
    aceita_termos: false
  });

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleTipoAjuda = (tipo) => {
    setFormData(prev => ({
      ...prev,
      tipos_ajuda: prev.tipos_ajuda.includes(tipo)
        ? prev.tipos_ajuda.filter(t => t !== tipo)
        : [...prev.tipos_ajuda, tipo]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.aceita_termos) {
      toast.error('Por favor, aceite os termos e condições');
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        tipo_doador: formData.tipo_doador,
        nome: formData.tipo_doador === 'Empresa' ? formData.empresa_nome : formData.nome,
        email_principal: formData.email_principal,
        telefone: formData.telefone || null,
        distrito_nome: formData.distrito || null,
        concelho: formData.concelho || null,
        nif: formData.nif || null,
        tipos_doacao_preferidos: formData.tipos_ajuda,
        notas: formData.mensagem || null,
        aceita_comunicacoes: formData.aceita_comunicacoes,
        origem_registo: 'pagina_quero_doar'
      };

      await apiClient.post('/public/doadores/registar', payload);
      
      setSubmitted(true);
      toast.success('Registo efetuado com sucesso!');
    } catch (error) {
      console.error('Erro ao registar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao efetuar registo');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.tipo_doador;
    }
    if (step === 2) {
      const nome = formData.tipo_doador === 'Empresa' ? formData.empresa_nome : formData.nome;
      return nome && formData.email_principal;
    }
    if (step === 3) {
      return formData.tipos_ajuda.length > 0 && formData.aceita_termos;
    }
    return false;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-green-200 bg-white/80 backdrop-blur">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              Obrigado pelo seu interesse!
            </h2>
            <p className="text-slate-600 mb-6">
              O seu registo foi recebido com sucesso. A nossa equipa irá entrar em contacto consigo brevemente para coordenar a sua ajuda.
            </p>
            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                <Mail className="w-4 h-4 inline mr-2" />
                Enviámos uma confirmação para <strong>{formData.email_principal}</strong>
              </p>
            </div>
            <Button onClick={() => window.location.href = '/'} variant="outline">
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50" data-testid="quero-doar-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-rose-600 to-amber-500 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 rounded-full p-4">
              <HeartHandshake className="w-12 h-12" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Quero Ajudar
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Junte-se à nossa rede de doadores e faça a diferença na vida de quem mais precisa em Portugal
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto py-6 px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-rose-600">500+</p>
              <p className="text-sm text-slate-600">Famílias Ajudadas</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-amber-600">150+</p>
              <p className="text-sm text-slate-600">Doadores Ativos</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-green-600">20</p>
              <p className="text-sm text-slate-600">Distritos Cobertos</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">50+</p>
              <p className="text-sm text-slate-600">Instituições Parceiras</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto pt-8 px-4">
        <div className="flex justify-center items-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                s === step 
                  ? 'bg-rose-600 text-white scale-110' 
                  : s < step 
                    ? 'bg-green-500 text-white' 
                    : 'bg-slate-200 text-slate-500'
              }`}>
                {s < step ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-20 h-1 rounded ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="text-center mb-8">
          <p className="text-sm text-slate-500">
            Passo {step} de 3: {
              step === 1 ? 'Tipo de Doador' : 
              step === 2 ? 'Dados de Contacto' : 
              'Como Quer Ajudar'
            }
          </p>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        {/* Step 1: Tipo de Doador */}
        {step === 1 && (
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-rose-600" />
                Como pretende doar?
              </CardTitle>
              <CardDescription>
                Selecione o tipo de doador que melhor o descreve
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {TIPOS_DOADOR.map(tipo => {
                  const Icon = tipo.icon;
                  const isSelected = formData.tipo_doador === tipo.value;
                  return (
                    <button
                      key={tipo.value}
                      onClick={() => updateForm('tipo_doador', tipo.value)}
                      className={`p-6 rounded-xl border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-rose-500 bg-rose-50' 
                          : 'border-slate-200 hover:border-rose-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full mb-3 flex items-center justify-center ${
                        isSelected ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <p className="font-medium text-slate-900">{tipo.label}</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Dados de Contacto */}
        {step === 2 && (
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-rose-600" />
                Dados de Contacto
              </CardTitle>
              <CardDescription>
                Precisamos dos seus dados para entrar em contacto
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.tipo_doador === 'Empresa' && (
                <div>
                  <Label>Nome da Empresa *</Label>
                  <Input
                    value={formData.empresa_nome}
                    onChange={e => updateForm('empresa_nome', e.target.value)}
                    placeholder="Ex: Empresa XYZ, Lda"
                  />
                </div>
              )}
              
              <div>
                <Label>{formData.tipo_doador === 'Empresa' ? 'Pessoa de Contacto' : 'Nome Completo'} *</Label>
                <Input
                  value={formData.nome}
                  onChange={e => updateForm('nome', e.target.value)}
                  placeholder="O seu nome completo"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email_principal}
                    onChange={e => updateForm('email_principal', e.target.value)}
                    placeholder="email@exemplo.pt"
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={formData.telefone}
                    onChange={e => updateForm('telefone', e.target.value)}
                    placeholder="912 345 678"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Distrito</Label>
                  <Select 
                    value={formData.distrito} 
                    onValueChange={v => updateForm('distrito', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {DISTRITOS_PORTUGAL.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Concelho</Label>
                  <Input
                    value={formData.concelho}
                    onChange={e => updateForm('concelho', e.target.value)}
                    placeholder="O seu concelho"
                  />
                </div>
              </div>

              {(formData.tipo_doador === 'Empresa' || formData.tipo_doador === 'Fundação') && (
                <div>
                  <Label>NIF / NIPC</Label>
                  <Input
                    value={formData.nif}
                    onChange={e => updateForm('nif', e.target.value)}
                    placeholder="123456789"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Como Quer Ajudar */}
        {step === 3 && (
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-rose-600" />
                Como Quer Ajudar?
              </CardTitle>
              <CardDescription>
                Selecione as formas como gostaria de contribuir
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-3">
                {TIPOS_AJUDA.map(tipo => {
                  const isSelected = formData.tipos_ajuda.includes(tipo.value);
                  return (
                    <div
                      key={tipo.value}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleTipoAjuda(tipo.value)}
                      onKeyDown={(e) => e.key === 'Enter' && toggleTipoAjuda(tipo.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all cursor-pointer ${
                        isSelected 
                          ? 'border-rose-500 bg-rose-50' 
                          : 'border-slate-200 hover:border-rose-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Checkbox checked={isSelected} />
                        <span className="font-medium text-slate-900">{tipo.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 pl-6">{tipo.desc}</p>
                    </div>
                  );
                })}
              </div>

              <div>
                <Label>Mensagem (opcional)</Label>
                <Textarea
                  value={formData.mensagem}
                  onChange={e => updateForm('mensagem', e.target.value)}
                  placeholder="Conte-nos mais sobre como gostaria de ajudar, disponibilidade, preferências..."
                  rows={3}
                />
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="comunicacoes"
                    checked={formData.aceita_comunicacoes}
                    onCheckedChange={v => updateForm('aceita_comunicacoes', v)}
                  />
                  <Label htmlFor="comunicacoes" className="text-sm cursor-pointer font-normal">
                    Aceito receber comunicações sobre necessidades urgentes e campanhas de solidariedade
                  </Label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="termos"
                    checked={formData.aceita_termos}
                    onCheckedChange={v => updateForm('aceita_termos', v)}
                  />
                  <Label htmlFor="termos" className="text-sm cursor-pointer font-normal">
                    Li e aceito a <a href="/privacidade" className="text-rose-600 underline">Política de Privacidade</a> e os <a href="/termos" className="text-rose-600 underline">Termos de Utilização</a> *
                  </Label>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 flex items-start gap-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-900">Os seus dados estão protegidos</p>
                  <p>Cumprimos o RGPD e nunca partilhamos os seus dados com terceiros sem o seu consentimento.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>
              Voltar
            </Button>
          ) : (
            <div />
          )}
          
          {step < 3 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>A processar...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submeter Registo
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Why Donate Section */}
      <div className="bg-white border-t py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-8">
            Porquê Doar Connosco?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-rose-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-rose-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Impacto Direto</h3>
              <p className="text-sm text-slate-600">
                A sua doação chega diretamente a quem precisa, sem intermediários
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                <Users className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Rede Certificada</h3>
              <p className="text-sm text-slate-600">
                Trabalhamos apenas com instituições certificadas e de confiança
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Transparência Total</h3>
              <p className="text-sm text-slate-600">
                Acompanhe o destino da sua doação e o impacto que gerou
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white py-8 px-4 text-center">
        <p className="text-slate-400 text-sm">
          © 2026 ENTRAJUDA - Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
